import React from 'react';
import { useLanguage } from '../../hooks/useAppLanguage';
import { pathFor, RouteName } from '../../routes/routesConfig';
import './CustomerStorySection.css';
import CustomerStoriesList from './CustomerStoriesList';

/* ════════════════════════════════════════════════════════════
   Customer stories on the landing pages. Every language uses the blog-style
   list (CustomerStoriesList); the Polish microsite keeps these two cards —
   Avara (water metering & billing) and Ikaalisten Kiinteistökonsultit
   (heating control) — in its own heading style and palette.
   ════════════════════════════════════════════════════════════ */

interface Card {
  route: RouteName;
  tone: 'blue' | 'cream';
  name: string;
  photo: string;
  alt: string;
}

const CARDS: Card[] = [
  {
    route: 'avaraWaterCase',
    tone: 'blue',
    name: 'Avara',
    photo: '/img/clients/Tuomas%20kuva%202026%204.webp',
    alt: 'Tuomas Rantsi, Avara',
  },
  {
    route: 'ikaalinenHeatingCase',
    tone: 'cream',
    name: 'Ikaalisten Kiinteistökonsultit',
    photo: '/img/clients/Valtteri%20Holmala.webp',
    alt: 'Valtteri Holmala, Ikaalisten Kiinteistökonsultit Oy',
  },
];

// Per language: section heading, and for each card its tag, a sentence split
// around the highlighted figure, and the link label.
interface Copy {
  title: string;
  lede: string;
  link: string;
  cards: { tag: string; before: string; pct: string }[];
}

const COPY: Record<string, Copy> = {
  pl: {
    title: 'Zmierzone wyniki z prawdziwych budynków',
    lede: 'Rozliczanie wody i sterowanie ogrzewaniem w praktyce — tak zmieniło się zużycie.',
    link: 'Zobacz całą historię',
    cards: [
      { tag: 'Opomiarowanie i rozliczenia wody', before: 'Przeszła na rozliczanie wody według zużycia i ograniczyła zużycie wody o około ', pct: '20%' },
      { tag: 'Sterowanie ogrzewaniem', before: 'Wdrożyło sterowanie ogrzewaniem w spółce mieszkaniowej. W pierwszym porównywalnym miesiącu zużycie ciepła sieciowego spadło o ', pct: '10%' },
    ],
  },
};

/**
 * Which site the section is rendered on. The Nordic market pages (sv, da) and
 * the Polish microsite have their own heading styles and palettes, so the header
 * is rendered with their classes and the cards are recoloured in CSS.
 */
type Variant = 'sv' | 'da' | 'pl';

const CustomerStorySection: React.FC<{ variant?: Variant; landingDesign?: boolean }> = ({
  variant,
  landingDesign,
}) => {
  const { language } = useLanguage();
  const c = COPY.pl;
  // The Finnish and English landing pages share one design (they both load
  // FinlandLandingPage.css): its own kicker + h2 section head, and the step
  // rail hidden while this full-bleed section is on screen. Finnish gets it
  // from its language; English passes the flag explicitly.
  const fi = landingDesign ?? language === 'fi';

  // Every landing page except the Polish microsite uses the blog-style list.
  if (variant !== 'pl') return <CustomerStoriesList lang={language} railClear={fi} />;

  return (
    <section
      className="cstories cstories--pl"
      aria-labelledby="cstories-title"
    >
      <div className="cstories__inner">
        <div className="cstories__head">
          <div className="ph-sec-head ph-sec-head--center">
            <h2 id="cstories-title">{c.title}</h2>
            <p>{c.lede}</p>
          </div>
        </div>

        <div className="cstories__grid">
          {CARDS.map((card, i) => {
            const t = c.cards[i];
            return (
              <div className="cstories__col" key={card.route}>
                <span className={`cstories__tag cstories__tag--${card.tone}`}>{t.tag}</span>
                <article className={`cstories__card cstories__card--${card.tone}`}>
                  <div className="cstories__media">
                    <img src={card.photo} alt={card.alt} loading="lazy" />
                  </div>
                  <div className="cstories__body">
                    <h3 className="cstories__name">{card.name}</h3>
                    <p className="cstories__desc">
                      {t.before}<span className="cstories__pct">{t.pct}</span>.
                    </p>
                  </div>
                  <div className="cstories__cta">
                    {/* Same visible text on both cards; the name tells them apart. */}
                    <a href={pathFor(card.route, language)} aria-label={`${c.link}: ${card.name}`}>{c.link}</a>
                  </div>
                </article>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CustomerStorySection;
